import React, { useState, useEffect } from "react";
import { Line } from "react-chartjs-2";
import { FaPlay, FaPause } from "react-icons/fa";

const RewardAdjustmentSimulator = () => {
  const [medianHistory, setMedianHistory] = useState([]);
  const [nominalReward, setNominalReward] = useState(44189);
  const [timeInterval, setTimeInterval] = useState(1000);
  const [BID_UPPER_BOUND, setBidUpperBound] = useState(950);
  const [BID_LOWER_BOUND, setBidLowerBound] = useState(500);
  const [SHORT_WINDOW, setShortWindow] = useState(5);
  const [LONG_WINDOW, setLongWindow] = useState(10);
  const [bidValue, setBidValue] = useState(50); // Bid value range state
  const [higherChange, setHigherChange] = useState(10); // Higher percentage change state
  const [lowerChange, setLowerChange] = useState(5); // Lower percentage change state
  const [nominalRewardHistory, setNominalRewardHistory] = useState([]); // Nominal reward history state
  const [lastChange, setLastChange] = useState(null); // Last percentage change
  const [epochCount, setEpochCount] = useState(1);
  const [isRunning, setIsRunning] = useState(true);

  useEffect(() => {
    if (!isRunning) return;

    const interval = setInterval(() => {
      const newBid = bidValue * 10; // Apply bid range value in a 0 to 1000 scale
      setMedianHistory((prev) => {
        const updatedHistory = [...prev, newBid];
        if (updatedHistory.length > 10) {
          updatedHistory.shift(); // Remove the oldest entry
        }
        return updatedHistory;
      });

      // Calculate nominalReward based on the function
      const [shouldUpdate, increase, adjustment] = calculate_reward_adjustment(
        medianHistory,
        nominalReward
      );

      let updatedNominalReward = nominalReward;
      let changePercent = 0;

      if (shouldUpdate) {
        updatedNominalReward = parseFloat(
          (increase
            ? nominalReward + adjustment
            : nominalReward - adjustment
          ).toFixed(0)
        );
        changePercent = parseFloat(
          ((adjustment / nominalReward) * 100).toFixed(2)
        );
        setLastChange(increase ? `+${changePercent}%` : `-${changePercent}%`);
      } else {
        setLastChange("0%");
      }

      setNominalReward(updatedNominalReward);
      setNominalRewardHistory((prev) => {
        const updatedHistory = [...prev, updatedNominalReward];
        if (updatedHistory.length > 10) {
          updatedHistory.shift(); // Remove the oldest entry
        }
        return updatedHistory;
      });

      // Increment the epoch count
      setEpochCount((prev) => prev + 1);
    }, timeInterval);

    return () => clearInterval(interval);
  }, [isRunning, medianHistory, nominalReward, timeInterval, bidValue]);

  const calculate_reward_adjustment = (medianHistory, nominalReward) => {
    let historyLength = medianHistory.length;
    let epochsAbove = 0;
    let epochsBelow = 0;

    for (let i = 0; i < Math.min(16, historyLength); i++) {
      let avgBid = medianHistory[i];

      if (avgBid >= BID_UPPER_BOUND) {
        epochsAbove++;
      } else if (avgBid <= BID_LOWER_BOUND) {
        epochsBelow++;
      }
    }

    if (nominalReward > 0) {
      if (epochsAbove > epochsBelow) {
        if (epochsAbove >= LONG_WINDOW) {
          return [true, false, nominalReward * (higherChange / 100)];
        } else if (epochsAbove >= SHORT_WINDOW) {
          return [true, false, nominalReward * (lowerChange / 100)];
        }
      } else {
        if (epochsBelow >= LONG_WINDOW) {
          return [true, true, nominalReward * (higherChange / 100)];
        } else if (epochsBelow >= SHORT_WINDOW) {
          return [true, true, nominalReward * (lowerChange / 100)];
        }
      }
    }

    return [false, false, 0];
  };

  const data = {
    labels: nominalRewardHistory.map(
      (_, index) =>
        `Epoch ${epochCount - nominalRewardHistory.length + index + 1}`
    ),
    datasets: [
      {
        label: "Nominal Reward",
        data: nominalRewardHistory,
        borderColor: "rgba(75,192,192,1)",
        yAxisID: "left-y-axis",
      },
      {
        label: "Bid Value",
        data: medianHistory,
        borderColor: "rgba(0, 123, 255, 1)",
        yAxisID: "right-y-axis",
      },
    ],
  };

  const options = {
    scales: {
      "left-y-axis": {
        type: "linear",
        position: "left",
        min: 0,
      },
      "right-y-axis": {
        type: "linear",
        position: "right",
        min: 0,
        max: 1000,
        ticks: {
          callback: (value) => `${value / 10}%`,
        },
      },
    },
    plugins: {
      annotation: {
        annotations: {
          upperBoundLine: {
            type: "line",
            yMin: BID_UPPER_BOUND,
            yMax: BID_UPPER_BOUND,
            borderColor: "rgba(255, 99, 132, 0.5)",
            borderWidth: 2,
            borderDash: [5, 5],
            label: {
              content: "BID_UPPER_BOUND",
              enabled: true,
              position: "end",
            },
            yScaleID: "right-y-axis",
          },
          lowerBoundLine: {
            type: "line",
            yMin: BID_LOWER_BOUND,
            yMax: BID_LOWER_BOUND,
            borderColor: "rgba(75, 192, 192, 0.5)",
            borderWidth: 2,
            borderDash: [5, 5],
            label: {
              content: "BID_LOWER_BOUND",
              enabled: true,
              position: "end",
            },
            yScaleID: "right-y-axis",
          },
        },
      },
    },
  };

  return (
    <>
      <div className="p-4 max-w-lg mx-auto">
        <h1 className="text-2xl font-bold mb-4 text-center">
          Reward Adjustment Simulator
        </h1>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">
              BID_UPPER_BOUND:
            </label>
            <input
              type="number"
              value={BID_UPPER_BOUND}
              onChange={(e) => setBidUpperBound(Number(e.target.value))}
              className="w-full p-2 border rounded-md"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">
              BID_LOWER_BOUND:
            </label>
            <input
              type="number"
              value={BID_LOWER_BOUND}
              onChange={(e) => setBidLowerBound(Number(e.target.value))}
              className="w-full p-2 border rounded-md"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">
              SHORT_WINDOW:
            </label>
            <input
              type="number"
              value={SHORT_WINDOW}
              onChange={(e) => setShortWindow(Number(e.target.value))}
              className="w-full p-2 border rounded-md"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">
              LONG_WINDOW:
            </label>
            <input
              type="number"
              value={LONG_WINDOW}
              onChange={(e) => setLongWindow(Number(e.target.value))}
              className="w-full p-2 border rounded-md"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">
              Higher Change (%):
            </label>
            <input
              type="number"
              value={higherChange}
              onChange={(e) => setHigherChange(Number(e.target.value))}
              className="w-full p-2 border rounded-md"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">
              Lower Change (%):
            </label>
            <input
              type="number"
              value={lowerChange}
              onChange={(e) => setLowerChange(Number(e.target.value))}
              className="w-full p-2 border rounded-md"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">
              Nominal Reward:
            </label>
            <input
              type="number"
              value={nominalReward}
              onChange={(e) => setNominalReward(Number(e.target.value))}
              className="w-full p-2 border rounded-md"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">
              Update Interval:
            </label>
            <input
              type="range"
              min="100"
              max="5000"
              value={timeInterval}
              step="100"
              onChange={(e) => setTimeInterval(Number(e.target.value))}
              className="w-full"
            />
            <span className="block text-center mt-1">
              {timeInterval / 1000}s
            </span>
          </div>
        </div>

        <div className="mb-4 flex items-center">
          <div className="flex-1 mr-4">
            <label className="block text-sm font-medium mb-1">
              Median Bid (%):
            </label>
            <input
              type="range"
              min="1"
              max="100"
              value={bidValue}
              step="1"
              onChange={(e) => setBidValue(Number(e.target.value))}
              className="w-full"
            />
            <span
              className={`block text-center mt-1 ${
                bidValue * 10 >= BID_UPPER_BOUND
                  ? "text-red-500"
                  : bidValue * 10 <= BID_LOWER_BOUND
                  ? "text-blue-500"
                  : ""
              }`}
            >
              {bidValue}%
            </span>
          </div>

          <div className="text-center">
            <button
              onClick={() => setIsRunning(!isRunning)}
              className="p-3 bg-blue-500 text-white rounded-full w-16 h-16 flex items-center justify-center shadow-lg hover:bg-blue-600 transition-colors"
            >
              {isRunning ? <FaPause size={20} /> : <FaPlay size={20} />}
            </button>
          </div>
        </div>
      </div>
      <div className="p-4 mx-auto">
        <div className="mb-4">
          <Line data={data} options={options} />
        </div>

        {lastChange !== null && (
          <div className="text-center mt-4">
            <p
              className={`text-lg font-medium ${
                lastChange.includes("-")
                  ? "text-red-500"
                  : lastChange.includes("+")
                  ? "text-blue-500"
                  : ""
              }`}
            >
              Last Nominal Reward Change: {lastChange}
            </p>
          </div>
        )}
      </div>
    </>
  );
};

export default RewardAdjustmentSimulator;
